import type { UserProfile, UserProfileService } from '@optimizely/optimizely-sdk'

function storageKey(userId: string): string {
  return `optimizely-ups-data-${userId}`
}

function emptyProfile(userId: string): UserProfile {
  return { user_id: userId, experiment_bucket_map: {} }
}

/**
 * Synchronous UserProfileService backed by `localStorage` (same keys as Optimizely docs).
 * On SSR or when storage is missing, `lookup` returns an empty bucket map and `save` is a no-op.
 */
export const optimizelyLocalStorageUserProfileService: UserProfileService = {
  lookup(userId: string): UserProfile {
    if (typeof localStorage === 'undefined')
      return emptyProfile(userId)

    try {
      const raw = localStorage.getItem(storageKey(userId))
      if (!raw)
        return emptyProfile(userId)

      const parsed = JSON.parse(raw) as Partial<UserProfile>
      return {
        user_id: parsed.user_id ?? userId,
        experiment_bucket_map: parsed.experiment_bucket_map ?? {},
      }
    }
    catch {
      return emptyProfile(userId)
    }
  },

  save(profile: UserProfile): void {
    if (typeof localStorage === 'undefined')
      return

    try {
      localStorage.setItem(storageKey(profile.user_id), JSON.stringify(profile))
    }
    catch (err) {
      console.warn('[Optimizely] User profile save failed:', err)
    }
  },
}
